import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Loader2, Star, Globe, Phone, MapPin, Plus, Check, Wand2 } from "lucide-react";
import { toast } from "sonner";
import PaywallModal from "@/components/PaywallModal";
import { LeadScoreBadge, BAND_STYLES } from "./LeadScoreBadge";

const BAND_FILTERS = [
  { key: "all", label: "All" },
  { key: "hot", label: "Hot" },
  { key: "strong", label: "Strong" },
  { key: "explore", label: "Exploring" },
  { key: "low", label: "Low" },
];

export const DiscoverTab = ({ niches, onLeadsAdded, initialSkill = "", initialCity = "" }) => {
  const { t } = useTranslation();
  const [skill, setSkill] = useState(initialSkill);
  const [city, setCity] = useState(initialCity);
  const [niche, setNiche] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bandFilter, setBandFilter] = useState("all");
  const [selected, setSelected] = useState({});
  const [adding, setAdding] = useState(false);
  const [paywall, setPaywall] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    if (!skill.trim() || !city.trim()) {
      toast.error("Enter your service and a city to search");
      return;
    }
    setLoading(true);
    setSelected({});
    try {
      const { data } = await api.post("/truffl/search", {
        skill: skill.trim(), city: city.trim(), niche: niche || undefined,
      });
      setResults(data.results);
      if (!data.results.length) toast.info("No results found — try a broader service or nearby area");
    } catch (err) {
      if (err?.response?.status === 402) {
        setPaywall({ kind: "truffl_search_limit", title: "Unlimited client discovery",
          body: err.response.data?.detail || "Upgrade to Pro for unlimited Truffl searches." });
      } else {
        toast.error(err?.response?.data?.detail || "Search failed — please try again");
        setResults([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggle = (r) => setSelected((s) => ({ ...s, [r.place_id]: s[r.place_id] ? undefined : r }));
  const picked = Object.values(selected).filter(Boolean);

  const addToPipeline = async (generate) => {
    if (!picked.length) return;
    if (picked.length > 5 && generate) {
      toast.error("Select up to 5 leads to generate missions at once");
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post("/truffl/leads", {
        leads: picked, skill: skill.trim(), city: city.trim(),
      });
      const ids = data.added.map((a) => a.lead_id);
      const dupes = data.added.filter((a) => a.duplicate).length;
      if (dupes) toast.info(`${dupes} lead(s) already in your pipeline — skipped duplicates`);
      if (generate) {
        toast.info("Generating personalised outreach missions…");
        await api.post("/truffl/generate", { lead_ids: ids.slice(0, 5) });
        toast.success(`Missions ready for ${ids.length} lead(s) 🎯`);
      } else {
        toast.success(`${ids.length} lead(s) added to your pipeline`);
      }
      setSelected({});
      onLeadsAdded?.();
    } catch (err) {
      if (err?.response?.status === 402) {
        setPaywall({ kind: "truffl_generate_limit", title: "Unlimited AI outreach",
          body: err.response.data?.detail || "Upgrade to Pro to generate unlimited outreach missions." });
      } else {
        toast.error(err?.response?.data?.detail || "Could not add leads");
      }
    } finally {
      setAdding(false);
    }
  };

  const shown = (results || []).filter((r) => bandFilter === "all" || r.band === bandFilter);

  return (
    <div className="space-y-6">
      <PaywallModal open={!!paywall} onOpenChange={(v) => !v && setPaywall(null)}
        kind={paywall?.kind} title={paywall?.title} body={paywall?.body} context="truffl" />
      <form onSubmit={search} className="rounded-2xl border border-border bg-card p-6" data-testid="truffl-search-form">
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label>{t("truffl.skill")}</Label>
            <Input className="mt-1.5" value={skill} onChange={(e) => setSkill(e.target.value)}
              placeholder="e.g. painting and decorating" data-testid="truffl-skill-input" />
          </div>
          <div>
            <Label>{t("truffl.city")}</Label>
            <Input className="mt-1.5" value={city} onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. London" data-testid="truffl-city-input" />
          </div>
          <div>
            <Label>{t("truffl.niche")}</Label>
            <Select value={niche} onValueChange={setNiche}>
              <SelectTrigger className="mt-1.5 h-10" data-testid="truffl-niche-select"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                {(niches || []).map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button type="submit" disabled={loading} className="mt-4" data-testid="truffl-search-btn">
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
          {loading ? t("truffl.searching") : t("truffl.find")}
        </Button>
      </form>

      {results !== null && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex gap-1.5" data-testid="truffl-band-filters">
              {BAND_FILTERS.map((b) => (
                <button key={b.key} onClick={() => setBandFilter(b.key)}
                  data-testid={`truffl-filter-${b.key}`}
                  className={`rounded-full px-3 py-1 text-xs transition-colors ${bandFilter === b.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
                  {b.label}
                </button>
              ))}
            </div>
            {picked.length > 0 && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={adding} onClick={() => addToPipeline(false)} data-testid="truffl-add-selected-btn">
                  <Plus className="mr-1 h-3.5 w-3.5" /> {t("truffl.add")} ({picked.length})
                </Button>
                <Button size="sm" disabled={adding} onClick={() => addToPipeline(true)}
                  className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="truffl-generate-btn">
                  {adding ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1 h-3.5 w-3.5" />}
                  {t("truffl.generate")}
                </Button>
              </div>
            )}
          </div>

          {shown.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="truffl-empty-state">
              No results{bandFilter !== "all" ? " in this score band" : ""}. Try a broader service, a nearby city, or a different niche.
            </div>
          ) : (
            <div className="grid gap-3 lg:grid-cols-2" data-testid="truffl-results">
              {shown.map((r) => {
                const isSel = !!selected[r.place_id];
                const bs = BAND_STYLES[r.band] || BAND_STYLES.low;
                return (
                  <button key={r.place_id} onClick={() => toggle(r)}
                    data-testid="truffl-lead-card"
                    className={`flex items-start gap-4 rounded-2xl border p-4 text-left transition-colors ${isSel ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}>
                    <LeadScoreBadge score={r.score} band={r.band} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium">{r.name}</span>
                        {isSel && <Check className="h-4 w-4 shrink-0 text-primary" />}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className={`rounded-full px-2 py-0.5 ${bs.chip}`}>{bs.label}</span>
                        {r.category && <span className="rounded-full bg-secondary px-2 py-0.5">{r.category}</span>}
                        {r.source === "companies_house" && (
                          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 font-semibold text-emerald-500" data-testid="new-business-badge">
                            NEW BUSINESS{r.incorporated ? ` · reg. ${r.incorporated}` : ""}
                          </span>
                        )}
                        {r.rating != null && (
                          <span className="flex items-center gap-0.5"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{r.rating} ({r.review_count || 0})</span>
                        )}
                      </div>
                      <div className="mt-1.5 flex items-start gap-1 text-xs text-muted-foreground">
                        <MapPin className="mt-0.5 h-3 w-3 shrink-0" /><span className="line-clamp-1">{r.address}</span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-3 text-xs">
                        {r.website ? (
                          <a href={r.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-1 text-primary hover:underline"><Globe className="h-3 w-3" /> Website</a>
                        ) : <span className="text-emerald-600">No website — easy pitch</span>}
                        {r.phone && <span className="flex items-center gap-1 text-muted-foreground"><Phone className="h-3 w-3" />{r.phone}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};
